import React, { useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, AlertCircle, Lightbulb, ClipboardList } from 'lucide-react';
import type { AnalysisFindingsWidgetData } from '../../../types/agentWidgets';

interface Props {
  data: AnalysisFindingsWidgetData;
}

const TYPE_COLORS: Record<string, string> = {
  GAP_ANALYSIS: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  CAUSATION_ANALYSIS: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  CONDITION_APPORTIONMENT: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  CHRONOLOGY: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
};

const SECTION_ICONS: Record<string, React.FC<{ className?: string }>> = {
  findings: AlertCircle,
  gaps: AlertCircle,
  recommendations: Lightbulb,
  summary: ClipboardList,
  methodology: BookOpen,
};

function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

const ContentSection: React.FC<{ sectionKey: string; value: unknown; defaultOpen?: boolean }> = ({ sectionKey, value, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = SECTION_ICONS[sectionKey] || ClipboardList;
  const label = formatKey(sectionKey);

  if (value == null) return null;

  const renderValue = (val: unknown): React.ReactNode => {
    if (typeof val === 'string') {
      return <p className="text-[11px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{val}</p>;
    }

    if (Array.isArray(val)) {
      return (
        <div className="space-y-1.5">
          {val.map((item, i) => (
            <div key={i} className="pl-2 border-l-2 border-purple-200 dark:border-purple-800">
              {typeof item === 'string' ? (
                <p className="text-[11px] text-gray-700 dark:text-gray-300">{item}</p>
              ) : typeof item === 'object' && item !== null ? (
                <div className="space-y-0.5">
                  {Object.entries(item as Record<string, unknown>).map(([k, v]) => (
                    <div key={k} className="text-[11px]">
                      <span className="font-medium text-gray-600 dark:text-gray-400">{formatKey(k)}: </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {typeof v === 'string' ? v : JSON.stringify(v)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-gray-700 dark:text-gray-300">{String(item)}</p>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (typeof val === 'object') {
      return (
        <div className="space-y-1 pl-2">
          {Object.entries(val as Record<string, unknown>).map(([k, v]) => (
            <div key={k} className="text-[11px]">
              <span className="font-medium text-gray-600 dark:text-gray-400">{formatKey(k)}: </span>
              <span className="text-gray-700 dark:text-gray-300">
                {typeof v === 'string' ? v : JSON.stringify(v)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return <p className="text-[11px] text-gray-700 dark:text-gray-300">{String(val)}</p>;
  };

  return (
    <div className="border-b border-purple-100 dark:border-purple-900/20 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 w-full px-2 py-1.5 text-left hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-colors"
      >
        {open ? <ChevronDown className="h-3 w-3 text-purple-500 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 text-purple-500 flex-shrink-0" />}
        <Icon className="h-3 w-3 text-purple-500 flex-shrink-0" />
        <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{label}</span>
        {Array.isArray(value) && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
            {value.length}
          </span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-2 max-h-[300px] overflow-y-auto">
          {renderValue(value)}
        </div>
      )}
    </div>
  );
};

// Known top-level keys to show first, in order
const PRIORITY_KEYS = ['summary', 'findings', 'gaps', 'recommendations', 'methodology', 'causation_assessment', 'conclusion'];

export const AnalysisFindingsWidget: React.FC<Props> = ({ data }) => {
  const [collapsed, setCollapsed] = useState(true);

  if (!data.content || Object.keys(data.content).length === 0) return null;

  const typeColor = TYPE_COLORS[data.objectiveType] || 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';

  // Sort content keys: priority keys first, then remaining alphabetically
  const contentKeys = Object.keys(data.content);
  const orderedKeys = [
    ...PRIORITY_KEYS.filter(k => contentKeys.includes(k)),
    ...contentKeys.filter(k => !PRIORITY_KEYS.includes(k)).sort(),
  ];

  return (
    <div className="bg-purple-50/30 dark:bg-purple-900/5 border-l-3 border-purple-400 dark:border-purple-600">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-colors"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400 flex-shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400 flex-shrink-0" />}
        <BookOpen className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
          {data.objectiveName}
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${typeColor}`}>
          {data.objectiveType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
        </span>
      </button>

      {!collapsed && (
        <div>
          {orderedKeys.map(key => (
            <ContentSection
              key={key}
              sectionKey={key}
              value={data.content[key]}
              defaultOpen={key === 'summary' || key === 'findings' || key === 'gaps'}
            />
          ))}
        </div>
      )}
    </div>
  );
};
